import { useEffect, useMemo, useRef, useState } from 'react';
import Card from '../components/Card';
import { useParams } from 'react-router-dom';
import { getSocket } from '../lib/socket';
import type { RoomState } from '../types';
import { loadIdentity, saveIdentity, ensureUserId } from '../lib/session';
import ConnectionBanner from '../components/ConnectionBanner';
import FlipCard from "../components/FlipCard";
import Statistics from "../components/Statistics";
import { t, useLocale } from '../i18n';

export default function Room() {
    useLocale(); // rerender on locale change
    const { roomId: rawRoomId } = useParams();
    const roomId = decodeURIComponent(rawRoomId || '');
    const socketRef = useRef(getSocket());
    const lastRoundRef = useRef<number | null>(null);
    const [state, setState] = useState<RoomState | null>(null);
    const [connState, setConnState] = useState<'connected' | 'connecting' | 'disconnected' | 'reconnected' | 'error'>('connecting');
    const [name, setName] = useState<string>('');
    const [userId, setUserId] = useState<string>('');
    const [nameCommitted, setNameCommitted] = useState(false);
    const [mySelected, setMySelected] = useState<string | null | undefined>(undefined);

    const deckLabel = state ? (() => {
        const key = `deck.${state.deckId}`;
        const translated = t(key);
        return translated === key ? (state.deckLabel || state.deckId || '') : translated;
    })() : '';

    // Base room_state subscription
    useEffect(() => {
        const socket = socketRef.current;

        function onRoomState(s: RoomState) {
            setState(s);

            // Falls neue Runde erkannt, lokale Auswahl zurücksetzen
            if (lastRoundRef.current === null || lastRoundRef.current !== s.round) {
                lastRoundRef.current = s.round;
                setMySelected(undefined);
                return;
            }

            // Beim Aufdecken den eigenen Wert aus dem Serverzustand übernehmen
            if (s.revealed) {
                const me = s.participants.find(p => p.userId === userId);
                if (me) setMySelected(me.value ?? null);
            }
            // Ansonsten (verdeckt, gleiche Runde) die lokale Auswahl NICHT überschreiben,
            // damit die blaue Markierung während des Schätzens erhalten bleibt.
        }

        socket.on('room_state', onRoomState);
        return () => { socket.off('room_state', onRoomState); };
    }, [userId]);

    // Load identity (per room) on mount
    useEffect(() => {
        if (!roomId) return;
        const id = loadIdentity(roomId);
        if (id) {
            setUserId(id.userId);
            setName(id.name);
            setNameCommitted(true);
        } else {
            setUserId(ensureUserId(''));
        }
    }, [roomId]);

    // Join when we have a name and roomId
    useEffect(() => {
        if (nameCommitted && roomId && userId) {
            socketRef.current.emit('join_room', { roomId, userId, name });
        }
    }, [nameCommitted, roomId, userId, name]);

    // Connection lifecycle handlers (reconnect → rejoin)
    useEffect(() => {
        const socket = socketRef.current;
        if (socket.connected) setConnState('connected');
        const onConnect = () => {
            setConnState(prev => prev === 'disconnected' ? 'reconnected' : 'connected');
            if (connState === "reconnected") {
                setTimeout(() => setConnState('connected'), 5000);
            }
            if (nameCommitted && roomId && userId) {
                socket.emit('join_room', { roomId, userId, name });
            }
        };
        const onDisconnect = () => setConnState('disconnected');
        const onError = () => setConnState('error');
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onError);
        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onError);
        };
    }, [nameCommitted, roomId, userId, name]);

    const participants = state?.participants ?? [];
    const selectedCount = useMemo(() => participants.filter(p => p.hasSelected).length, [participants]);
    const all = participants.length;

    function reveal() {
        if (!state) return;
        socketRef.current.emit('reveal', { roomId: state.id });
    }

    function newRound() {
        if (!state) return;
        setMySelected(undefined);
        socketRef.current.emit('new_round', { roomId: state.id });
    }

    function select(value: string | null) {
        if (!state) return;
        setMySelected(value ?? null);
        socketRef.current.emit('select_card', { roomId: state.id, userId, value });
    }

    function copyLink() {
        const url = window.location.href;
        navigator.clipboard.writeText(url).then(() => {
            alert(t('room.link.copied', { url }));
        });
    }

    function getBgColorMe(isMe: boolean, selected: boolean) {
        if (!isMe) {
            return "";
        }
        return selected ? "!bg-green-100" : "!bg-blue-100";
    }
    function getFormattedNumberOrDash(value: number | null): string {
        if (value === null || value === undefined) return '-';
        if (Number.isInteger(value)) return value.toString();
        return value.toFixed(1);
    }

    // --- Statistics (computed only when revealed) ---
    const stats = useMemo(() => {
        if (!state?.revealed) return null;

        const allParticipants = state.participants ?? [];
        const numericVotes = allParticipants
            .map(p => p.value)
            .filter(v => v !== null && v !== undefined)
            .map(v => Number(v))
            .filter(n => Number.isFinite(n));

        const n = numericVotes.length;
        const totalCount = allParticipants.length;
        if (n === 0) {
            const distMap = new Map<string, number>();
            for (const p of allParticipants) {
                const key = p.value === null ? '—' : String(p.value);
                distMap.set(key, (distMap.get(key) ?? 0) + 1);
            }
            return { n, totalCount, avg: null, median: null, min: null, max: null, dist: Array.from(distMap.entries()), consensus: null };
        }

        const sorted = [...numericVotes].sort((a, b) => a - b);
        const sum = sorted.reduce((a, b) => a + b, 0);
        const avg = sum / n;
        const median = (n % 2 === 1) ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
        const min = sorted[0];
        const max = sorted[n - 1];

        const distMap = new Map<string, number>();
        for (const p of allParticipants) {
            const key = p.value === null ? '—' : String(p.value);
            distMap.set(key, (distMap.get(key) ?? 0) + 1);
        }

        const firstVal = allParticipants.length ? allParticipants[0].value : null;
        const consensus = (firstVal !== null && allParticipants.every(p => p.value === firstVal)) ? String(firstVal) : null;

        const deckOrder = new Map<string, number>();
        (state?.deck ?? []).forEach((v, i) => deckOrder.set(String(v), i));

        const dist = Array.from(distMap.entries()).sort((a, b) => {
            const aKey = a[0];
            const bKey = b[0];
            if (aKey === '—' && bKey === '—') return 0;
            if (aKey === '—') return 1;
            if (bKey === '—') return -1;

            const aIdx = deckOrder.get(aKey);
            const bIdx = deckOrder.get(bKey);
            const aHas = typeof aIdx === 'number';
            const bHas = typeof bIdx === 'number';
            if (aHas && bHas) return aIdx - bIdx;
            if (aHas) return -1;
            if (bHas) return 1;
            return aKey.localeCompare(bKey);
        });

        return { n, totalCount, avg, median, min, max, dist, consensus };
    }, [state]);

    return (
        <div className="space-y-6">
            <ConnectionBanner state={connState} />

            {/* Name prompt */}
            {!nameCommitted && (
                <div className="card p-4 flex items-center justify-between gap-3">
                    <div className="flex-1">
                        <label className="label" htmlFor="display-name">{t('room.name.label')}</label>
                        <input id="display-name" className="input w-full" placeholder={t('room.name.placeholder')} value={name} onChange={e => setName(e.target.value)} />
                    </div>
                    <button
                        className="btn-primary mt-6"
                        disabled={!name.trim()}
                        onClick={() => { setNameCommitted(true); saveIdentity(roomId, { userId, name }); }}
                    >
                        {t('room.join.button')}
                    </button>
                </div>
            )}

            {/* Room header */}
            {state && (
                <div className="flex items-center justify-between room-header">
                    <div>
                        <h2 className="text-2xl font-semibold room-info-text">{t('room.header.room')}: <span className="text-indigo-700 room-info-text-highlight">{state.title}</span></h2>
                        <p className="text-slate-600 room-info-text">
                            {t('room.header.round')} {state.round} · {selectedCount}/{all} {t('room.header.selected')} · {state.revealed ? t('room.header.revealed') : t('room.header.hidden')}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button className="btn" onClick={copyLink}>{t('room.actions.share')}</button>
                        {!state.revealed && (
                            <button className="btn-primary" onClick={reveal}>{t('room.actions.reveal')}</button>
                        )}
                        <button className="btn" onClick={newRound}>{t('room.actions.newRound')}</button>
                    </div>
                </div>
            )}

            {/* Participants */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {participants.map(p => {
                    const isMe = p.userId === userId;
                    return (
                        <FlipCard
                            key={p.userId}
                            participant={p}
                            isRevealed={state?.revealed ?? false}
                            isMe={isMe}
                        />
                    );
                })}
            </div>

            {/* Statistics - Now using separate component */}
            {state?.revealed && stats && <Statistics stats={stats} />}

            {/* Deck */}
            {state && (
                <div className="card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="label">{t('room.deck.title', { deck: deckLabel })}</div>
                        <div className="text-sm text-slate-500">{state.revealed ? t('room.deck.hint.revealed') : t('room.deck.hint.hidden')}</div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {state.deck.map(v => (
                            <Card
                                key={v}
                                selected={mySelected === v}
                                onClick={() => select(v)}
                            >
                                {v}
                            </Card>
                        ))}
                        {/* Clear selection */}
                        <button
                            className={[
                                'card w-16 h-24 sm:w-20 sm:h-28 flex items-center justify-center text-sm text-slate-600',
                                mySelected === null ? 'ring-2 ring-indigo-600 scale-[1.03]' : 'hover:scale-[1.02]'
                            ].join(' ')}
                            onClick={() => select(null)}
                        >{t('room.deck.clear')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
